import React, { useEffect } from 'react';
import { Form, Input, Button, Row, Col, InputNumber, Select, Space, Card } from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { useCustomer } from '../../store/customerContext';
import { Customer } from '../../types/customer';
import { useTranslation } from "react-i18next";
import CitySelect from '../city/CitySelect';
import { useAuth } from '../../store/AuthContext';
import { useCurrency } from '../../store/currencyContext';

interface CustomerFormProps {
  editCustomer?: Customer;
  onFinishEdit?: () => void;
}

const CustomerForm: React.FC<CustomerFormProps> = ({ editCustomer, onFinishEdit }) => {
  const [form] = Form.useForm();
  const { addCustomer, updateCustomer } = useCustomer();
  const { t, i18n } = useTranslation();
  const { hasPermission } = useAuth();
  const { currencies } = useCurrency();

  const onFinish = (values: Omit<Customer, "id">) => {
    if (editCustomer) {
      updateCustomer({ ...editCustomer, ...values });
      onFinishEdit?.();
      form.resetFields();
    } else {
      const newCustomer: Customer = {
        ...values,
      };
      addCustomer(newCustomer);
      form.resetFields();
    }
  };

  useEffect(() => {
    const cityId = typeof editCustomer?.city === 'object' && '_id' in editCustomer.city
      ? editCustomer.city._id
      : editCustomer?.city;

    const sanitizedCustomer = {
      ...editCustomer,
      city: cityId
    };
    if (editCustomer) {
      form.setFieldsValue(sanitizedCustomer);
    }
  }, [editCustomer, form]);

  const currencyOptions = currencies.map(c => ({
    value: c.code,
    label: `${c.code} (${c.symbol})`
  }));

  return (
    <div style={{ direction: i18n.language === 'ar' || i18n.language === 'ckb' ? 'rtl' : 'ltr' }}>
      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Form.Item
              name="name"
              label={t('name')}
              rules={[{ required: true, message: t('please_enter_name') as string }]}
            >
              <Input placeholder={t('name') as string} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="phone"
              label={t('phone')}
              rules={[{ required: true, message: t('please_enter_phone') as string }]}
            >
              <Input placeholder={t('phone') as string} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="address"
              label={t('address')}
              rules={[{ required: true, message: t('please_enter_address') as string }]}
            >
              <Input placeholder={t('address') as string} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="email"
              label={t('email')}
              rules={[{ type: 'email', message: t('please_enter_valid_email') as string }]}
            >
              <Input placeholder={t('email') as string} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="customerCode"
              label={t('customerCode')}
              rules={[{ required: true, message: t('please_enter_customerCode') as string }]}
            >
              <Input placeholder={t('customerCode') as string} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="city"
              label={t('city')}
              rules={[{ required: true, message: t('please_enter_city') as string }]}
            >
              <CitySelect />
            </Form.Item>
          </Col>
          {/* BALANCES */}
          {/* <Col span={24}>
            <Card
              title={t('balances')}
              style={{ marginTop: 8, marginBottom: 0, borderRadius: 10, background: "#fafbff" }}
              styles={{ body: { paddingBottom: 8 } }}
            >
              <Form.List name="balances" initialValue={editCustomer?.balances ?? []}>
                {(fields, { add, remove }) => (
                  <>
                    <Row gutter={8} style={{ fontWeight: 500, marginBottom: 4 }}>
                      <Col span={12}>{t('currency')}</Col>
                      <Col span={8}>{t('balance')}</Col>
                      <Col span={4}></Col>
                    </Row>
                    {fields.map(({ key, name, ...restField }) => (
                      <Row key={key} gutter={8} style={{ marginBottom: 6 }}>
                        <Col span={12}>
                          <Form.Item
                            {...restField}
                            name={[name, 'currency']}
                            rules={[{ required: true, message: t('please_select_currency') as string }]}
                            style={{ marginBottom: 0 }}
                          >
                            <Select
                              options={currencyOptions}
                              placeholder={t('currency')}
                              showSearch
                              allowClear
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item
                            {...restField}
                            name={[name, 'balance']}
                            rules={[{ required: true, message: t('please_enter_balance') as string }]}
                            style={{ marginBottom: 0 }}
                          >
                            <InputNumber min={0} placeholder={t('balance') as string} style={{ width: '100%' }} />
                          </Form.Item>
                        </Col>
                        <Col span={4} style={{ display: 'flex', alignItems: 'center' }}>
                          <Button
                            type="link"
                            danger
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(name)}
                          />
                        </Col>
                      </Row>
                    ))}
                    <Button
                      type="dashed"
                      block
                      icon={<PlusOutlined />}
                      onClick={() => add()}
                      style={{ marginTop: 4 }}
                    >
                      {t('add_balance')}
                    </Button>
                  </>
                )}
              </Form.List>
            </Card>
          </Col> */}
          <Col span={24}>
            <Form.Item>
              <Button type="primary" htmlType="submit" block>
                {editCustomer ? (hasPermission('customer.update') && t('update_customer')) : (hasPermission('customer.create') && t('add_customer'))}
              </Button>
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </div>
  );
};

export default CustomerForm;
